import { Product } from  "../../stores/appReportsStore";
import { Report as FrontendReport } from "../../stores/appReportsStore";
import { getAccessToken } from "../authentication/supabaseService";
import { calculateTotalSpent } from "../import/importProductIdsService";

interface ReportTotals {
  currentTotal: number;
  newTotal: number;
  savings: number;
  savingsPercentage: number;
}

export interface BackendReport {
    id: string;
    user_d: string;
    name: string;
    start_date: string,
    end_date: string,
    sourcing_data: unknown;
    restaurant_id: string;
    created_at: string;
    updated_at: string;
}

export const calculateReportTotals = (
  productsMap: Record<string, Product>,
  selectedMap: Record<string, Product>
): ReportTotals => {
  const products = Object.values(productsMap || {});
  const selected = Object.values(selectedMap || {});
  const currentTotal = products.reduce((sum, p) => sum + calculateTotalSpent(p), 0);
  const newTotal = selected.length
    ? selected.reduce((sum, p) => sum + calculateTotalSpent(p), 0)
    : 0;
  const savings = currentTotal - newTotal;
  const savingsPercentage = currentTotal > 0 ? (savings / currentTotal) * 100 : 0;
  return { currentTotal, newTotal, savings, savingsPercentage };
};


function mapBackendReportToFrontend(report: BackendReport): FrontendReport {
    return {
      id: report.id,
      userId: report.user_d,           
      name: report.name,
      startDate: report.start_date,    
      endDate: report.end_date,        
      sourcingData: report.sourcing_data, 
      restaurantId: report.restaurant_id,
      createdAt: report.created_at,    
      updatedAt: report.updated_at,    
    };
  }


export async function createReport(
    payload: { name: string; startDate: string; endDate: string; sourcingData: any; restaurantId: string }
  ): Promise<FrontendReport> {
    const token = await getAccessToken();
    const res = await fetch(`/api/reports/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Failed to save report');
  
    const backendReport: BackendReport = await res.json();
  
    const frontendReport: FrontendReport = mapBackendReportToFrontend(backendReport);
  
    return frontendReport;
  }

  
  export async function updateReport(
    id: string,
    payload: { name: string; startDate: string; endDate: string; sourcingData: any; restaurantId: string }
  ) {
    const token = await getAccessToken();
    const res = await fetch(`/api/reports/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Failed to update report');
    return mapBackendReportToFrontend(await res.json());
  }


  export async function fetchReports(): Promise<FrontendReport[]> {
    const token = await getAccessToken();
    const res = await fetch(`/api/reports/getAll`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
  
    if (!res.ok) {
      throw new Error(`Failed to fetch reports: ${res.status} ${res.statusText}`);
    }
  
    const backendReports: BackendReport[] = await res.json();
  
    const frontendReports: FrontendReport[] = backendReports.map((r) =>
      mapBackendReportToFrontend(r)
    );
  
    return frontendReports;
  }

  export async function deleteReport(id: string): Promise<void> {
    const token = await getAccessToken();
    const res = await fetch(`/api/reports/delete/${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    if (!res.ok) throw new Error(`Failed to delete report: ${res.status}`);
  }